import {
  Alert,
  Box,
  Button,
  Heading,
  IconButton,
  useDisclosure,
} from '@hover/blueprint';
import { Link } from '@hover/blueprint/chakra';
import { iEye, iEyeOff } from '@hover/icons';
import React, { useContext, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router';
import type { InferType } from 'yup';

import { isSsoError } from 'src/modules/onboarding/components/Login/Component';
import { OnboardingContext } from 'src/modules/onboarding/components/OnboardingContext';
import { useOnboardingError } from 'src/modules/onboarding/hooks/useOnboardingError';
import { makePasswordValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import {
  OnboardingTextField,
  Form as SharedForm,
} from 'src/shared/components/forms';
import { defaultLocalStorageAdapter } from 'src/shared/graphql';
import { useAuthenticateMutation } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useEffectOnMount,
  useTranslate,
} from 'src/shared/hooks';
import { Route, Route as Routes } from 'src/shared/route';
import { buttonPressed, pageViewed } from 'src/shared/telemetry';
import { isNetworkError } from 'src/shared/utils/graphql';

import { LoginAnotherWay } from './LoginAnotherWay';

export type PasswordFormData = InferType<
  ReturnType<typeof makePasswordValidationSchema>
>;

interface PasswordFormFieldsProps {
  hasError?: boolean;
  isAuthenticating?: boolean;
  hasNetworkError?: boolean;
  onForgotPassword?: () => void;
}

export const PasswordFormFields = ({
  hasError = false,
  isAuthenticating = false,
  hasNetworkError = false,
  onForgotPassword,
}: PasswordFormFieldsProps) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const { isOpen, onToggle } = useDisclosure();
  const history = useHistory();
  const { search } = useLocation();
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { watch } = useFormContext<Record<string, string>>();
  const [_state, dispatch] = useContext(OnboardingContext);

  useEffectOnMount(() => {
    pageViewed({
      page_or_screen_name: 'Login-email_password',
      feature: 'onboarding',
    });
  });

  const email = watch('email');

  const navigateToForgotPassword = () => {
    buttonPressed({
      button_text: 'Forgot password?',
      page_or_screen_name: 'Login-email_password',
      feature: 'onboarding',
    });
    dispatch({ email });
    history.push(`${Route.SIGN_UP_ONBOARDING.path}/reset${search}`);
  };

  return (
    <>
      <OnboardingTextField
        name="email"
        placeholder={t('onboarding.email')}
        type="email"
        width={{ base: 1, tablet: '400px' }}
      />
      <OnboardingTextField
        elementAfter={
          <IconButton
            color="neutral"
            fill="minimal"
            icon={isOpen ? iEyeOff : iEye}
            label={
              isOpen
                ? t('passwordInput.showPassword')
                : t('passwordInput.hidePassword')
            }
            onClick={onToggle}
            shape="square"
          />
        }
        name="password"
        placeholder={t('onboarding.password')}
        type={isOpen ? 'text' : 'password'}
        width={{ base: 1, tablet: '400px' }}
      />
      {hasError && (
        <Alert status="error">{t('onboarding.incorrectPassword')}</Alert>
      )}
      {hasNetworkError && (
        <Alert status="error">
          {t('onboarding.somethingWentWrongTryAgain')}
        </Alert>
      )}
      <Link
        as={Box}
        color="neutral.500"
        onClick={onForgotPassword || navigateToForgotPassword}
        sx={{
          cursor: 'pointer',
        }}
        textDecoration="underline"
      >
        {t('onboarding.forgotPassword')}
      </Link>

      <Button
        isLoading={isAuthenticating}
        mt={700}
        size="large"
        type="submit"
        width="100%"
      >
        {t('onboarding.login')}
      </Button>
    </>
  );
};

export const Password = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const [hasError, setHasError] = useState(false);
  const [hasNeworkError, setHasNetworkError] = useState(false);
  const { logError } = useOnboardingError();
  const history = useHistory();
  const { search } = useLocation();
  const [state, dispatch] = useContext(OnboardingContext);
  const [authenticate] = useAuthenticateMutation();
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);

  const validationSchema = makePasswordValidationSchema(t);
  const initialValues = { email: state?.email ?? '', password: '' };

  const onSubmit = async (formData: PasswordFormData) => {
    buttonPressed({
      button_text: 'Log in',
      page_or_screen_name: 'Email_input_password',
      feature: 'onboarding',
    });
    const { email, password } = formData;
    if (!email || !password) return;

    dispatch({ email });
    setIsAuthenticating(true);

    const { data } = await authenticate({
      variables: { email, password },
      refetchQueries: ['DetectUserSession'],
      onError: e => {
        const hasNetworkError = isNetworkError(e);
        setIsAuthenticating(false);
        if (isSsoError(e.graphQLErrors)) {
          history.push(`${Routes.SIGN_UP_ONBOARDING.path}/login/sso${search}`);
        } else if (hasNetworkError) {
          // We can skip logging on this error since network errors are expected
          setHasNetworkError(true);
        } else {
          setHasError(true);
          logError(e);
        }
      },
    });

    if (data?.authenticate?.user) {
      setIsAuthenticating(false);
      const {
        jwt: { accessToken, refreshToken },
      } = data.authenticate;
      void defaultLocalStorageAdapter.setAccessToken(accessToken);
      void defaultLocalStorageAdapter.setRefreshToken(refreshToken);
    }
  };

  return (
    <Box alignItems="center" height="100dvh" justifyContent="center" mx={500}>
      <Box flexDirection="column" width="750">
        <Heading
          color="text.primary"
          size={{ base: 700, tablet: 900 }}
          textAlign="center"
        >
          {t('onboarding.login')}
        </Heading>

        <Box flexDirection="column" mt={200}>
          <SharedForm<PasswordFormData>
            display="flex"
            height="100%"
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
          >
            <PasswordFormFields
              hasError={hasError}
              hasNetworkError={hasNeworkError}
              isAuthenticating={isAuthenticating}
            />
          </SharedForm>
        </Box>

        <LoginAnotherWay pageOrScreenName="Login-email_password" />
      </Box>
    </Box>
  );
};
